import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, CheckCircle2, ChevronRight, Phone, Globe, MapPin, CalendarDays, ExternalLink, Camera,
} from '../ui/Icon';
import { Icon } from '../ui/Icon';
import Spinner from '../ui/Spinner';
import DeleteVenueButton from './DeleteVenueButton';
import SearchPhotoCarousel from './SearchPhotoCarousel';
import { CAT_META } from '../../utils/mapConstants';
import type { Venue } from '../../types';
import type { OpeningHoursPeriod } from '../../api/places.api';
import { useDateLocale } from '../../utils/dateLocale';
import { formatWeeklyHours } from '../../utils/openingHours';

export interface PinnedVenue extends Venue { lat: number; lng: number }

export interface PlaceDetail {
  photos: string[];
  rating?: number;
  userRatingsTotal?: number;
  priceLevel?: number;
  openNow?: boolean | null;
  openingHoursPeriods?: OpeningHoursPeriod[];
  phone?: string;
  website?: string;
}

interface Props {
  venue: PinnedVenue;
  pinScreenPos: { x: number; y: number } | null;
  /** `undefined` = never requested (lean card), `null` = requested, nothing found. */
  placeDetail: PlaceDetail | null | undefined;
  detailsLoading: boolean;
  onRevealDetails: () => void;
  cardDragOffset: { dx: number; dy: number };
  setCardDragOffset: React.Dispatch<React.SetStateAction<{ dx: number; dy: number }>>;
  venueTodays: Map<string, number[]>;
  onClose: () => void;
  /** Absent = read-only: a community trip's place, which the viewer can look at
   *  (photos included, on request) but not mark, schedule or delete. */
  actions?: VenueCardActions;
}

export interface VenueCardActions {
  onToggleVisited: () => Promise<void>;
  onAddToItinerary: () => void;
  onDelete: () => Promise<void>;
}

export default function VenueDetailCard({
  venue: v, pinScreenPos, placeDetail: place, detailsLoading, onRevealDetails,
  cardDragOffset, setCardDragOffset,
  venueTodays, onClose, actions,
}: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const dragStartRef = useRef<{ mx: number; my: number; dx: number; dy: number } | null>(null);

  const meta = CAT_META[v.category];
  const loading = detailsLoading;
  const canReveal = place === undefined && !loading;
  const photos = place?.photos ?? [];
  const priceStr = place?.priceLevel != null ? '€'.repeat(place.priceLevel + 1) : null;
  // Prefer the numeric periods (language-agnostic) over Google's prose, which
  // comes back in whatever language the cached lookup happened to use.
  const weekHours = place?.openingHoursPeriods?.length
    ? formatWeeklyHours(place.openingHoursPeriods, dateLocale)
    : null;
  const todayHours = weekHours?.find(d => d.isToday)?.hours ?? null;

  const CARD_W = Math.min(416, window.innerWidth - 32);
  const GAP = 14;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let cardStyle: React.CSSProperties;
  let lineHeight = 0;
  let lineX = 0;
  let lineTop = 0;

  if (pinScreenPos && pinScreenPos.x > 0 && pinScreenPos.y > 0 && pinScreenPos.x < vw && pinScreenPos.y < vh) {
    const left = Math.max(16, Math.min(vw - CARD_W - 16, pinScreenPos.x - CARD_W / 2));
    const MAX_CARD_H = Math.min(Math.round(vh * 0.7), 520);
    const canShowBelow = (pinScreenPos.y + GAP + MAX_CARD_H) <= (vh - 16);
    const canShowAbove = (pinScreenPos.y - GAP - MAX_CARD_H) >= 16;
    const above = !canShowBelow;
    const centred = above && !canShowAbove;
    const base = { position: 'fixed' as const, zIndex: 9999, width: CARD_W, left: left + cardDragOffset.dx };

    // The card is pinned by whichever edge faces the pin — never by a top
    // computed from MAX_CARD_H. That guess only lined up for a card that
    // happened to be exactly that tall: a short one (no opening hours) hung
    // ~140px above its pin with the connector line dangling on its own.
    if (centred) {
      // Nowhere to sit next to the pin, so there is nothing to connect to
      // either: no line, or it would float alone in the middle of the map.
      cardStyle = { ...base, top: Math.max(16, Math.round((vh - MAX_CARD_H) / 2)) + cardDragOffset.dy };
    } else {
      lineHeight = GAP;
      lineX = pinScreenPos.x;
      lineTop = above ? pinScreenPos.y - GAP : pinScreenPos.y;
      cardStyle = above
        ? { ...base, bottom: (vh - pinScreenPos.y + GAP) - cardDragOffset.dy }
        : { ...base, top: pinScreenPos.y + GAP + cardDragOffset.dy };
    }
  } else {
    cardStyle = { position: 'fixed', zIndex: 9999, width: CARD_W, left: Math.round(vw / 2 - CARD_W / 2) + cardDragOffset.dx, top: vh - 76 - 400 + cardDragOffset.dy };
  }

  function onDragStart(e: React.MouseEvent) {
    if (e.button !== 0) return;
    e.preventDefault();
    dragStartRef.current = { mx: e.clientX, my: e.clientY, dx: cardDragOffset.dx, dy: cardDragOffset.dy };
    function onMove(me: MouseEvent) {
      if (!dragStartRef.current) return;
      setCardDragOffset({ dx: dragStartRef.current.dx + me.clientX - dragStartRef.current.mx, dy: dragStartRef.current.dy + me.clientY - dragStartRef.current.my });
    }
    function onUp() { dragStartRef.current = null; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  const scheduledDays = venueTodays.get(v.id);

  return (
    <div style={{ ...cardStyle, position: 'fixed' }}>
      {lineHeight > 0 && (
        <div style={{ position: 'fixed', left: lineX, top: lineTop, width: 2, height: lineHeight, backgroundColor: 'rgba(148,163,184,0.5)', zIndex: 9998, transform: 'translateX(-50%)' }} />
      )}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden">

        {/* Drag handle */}
        <div onMouseDown={onDragStart} className="cursor-grab active:cursor-grabbing select-none" title={t('common.dragToMove')}>
          {loading && <div className="h-44 bg-slate-100 dark:bg-slate-800 flex items-center justify-center"><Spinner size="md" /></div>}
          {!loading && photos.length > 0 && <SearchPhotoCarousel key={v.id} photos={photos} name={v.name} tripId={v.tripId} />}
          {!loading && photos.length === 0 && (
            <div className="h-20 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${meta.markerColor}22, ${meta.markerColor}10)` }}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: `${meta.markerColor}30` }}>
                  <Icon as={meta.Icon} size={20} className={meta.color} />
                </div>
                <span className={`text-sm font-semibold ${meta.color}`}>{v.customCategory ?? t(`venues.categories.${v.category}`)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4">
          {/* Title row */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0">
              <p className="font-bold text-slate-900 dark:text-slate-100 text-base leading-snug">{v.name}</p>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 ${meta.color}`}>
                  <Icon as={meta.Icon} size={12} /> {v.customCategory ?? t(`venues.categories.${v.category}`)}
                </span>
                {v.zone && (
                  <span className="text-[11px] font-semibold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-2 py-0.5 rounded-full">{v.zone}</span>
                )}
                {v.visited && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                    <Icon as={CheckCircle2} size={12} /> {t('venues.visited')}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose}
              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer mt-0.5">
              <Icon as={X} size={14} />
            </button>
          </div>

          {/* Rating + price */}
          {(place?.rating || priceStr) && (
            <div className="flex items-center gap-3 mb-3">
              {place?.rating && (
                <div className="flex items-center gap-1.5">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <svg key={s} className="w-3 h-3" viewBox="0 0 24 24"
                        fill={place.rating! >= s ? '#f59e0b' : place.rating! >= s - 0.5 ? 'url(#half)' : '#cbd5e1'}>
                        <defs><linearGradient id="half"><stop offset="50%" stopColor="#f59e0b"/><stop offset="50%" stopColor="#cbd5e1"/></linearGradient></defs>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                  </div>
                  <span className="text-xs font-bold text-amber-500 dark:text-amber-400">{place.rating.toFixed(1)}</span>
                  {place.userRatingsTotal && (
                    <span className="text-[11px] text-slate-400 dark:text-slate-500">({place.userRatingsTotal.toLocaleString()})</span>
                  )}
                </div>
              )}
              {priceStr && (
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full tracking-wide">{priceStr}</span>
              )}
            </div>
          )}

          {/* Details */}
          <div className="space-y-2 mb-3">
            {(place?.openNow != null || todayHours) && (
              <div className="flex items-start gap-2.5">
                <div className={`shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${place?.openNow ? 'bg-green-500' : place?.openNow === false ? 'bg-red-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    {place?.openNow != null && (
                      <span className={`text-xs font-semibold ${place.openNow ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {place.openNow ? t('venues.openNow') : t('venues.closedNow')}
                      </span>
                    )}
                    {todayHours && <span className="text-xs text-slate-500 dark:text-slate-400">{todayHours}</span>}
                  </div>
                  {weekHours && (
                    <details className="mt-1 group">
                      <summary className="text-[11px] text-slate-400 dark:text-slate-500 cursor-pointer hover:text-teal-600 dark:hover:text-teal-400 select-none list-none flex items-center gap-1">
                        <Icon as={ChevronRight} size={12} className="transition-transform group-open:rotate-90" />
                        {t('venues.fullSchedule')}
                      </summary>
                      <div className="mt-1.5 grid grid-cols-1 gap-0.5 pl-4">
                        {weekHours.map(({ day, hours, isToday }) => (
                          <div key={day} className={`flex justify-between gap-2 text-[11px] py-0.5 ${isToday ? 'font-semibold text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'}`}>
                            <span className="shrink-0 capitalize">{day}</span>
                            <span className="text-right">{hours ?? t('venues.closedDay')}</span>
                          </div>
                        ))}
                      </div>
                      <p className="mt-1.5 pl-4 text-[10px] italic text-slate-400 dark:text-slate-500">
                        {t('venues.hoursDisclaimer')}
                      </p>
                    </details>
                  )}
                </div>
              </div>
            )}
            {place?.phone && (
              <div className="flex items-center gap-2.5">
                <Icon as={Phone} size={14} className="text-slate-400 dark:text-slate-500 shrink-0" />
                <a href={`tel:${place.phone}`} className="text-xs text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium">{place.phone}</a>
              </div>
            )}
            {place?.website && (
              <div className="flex items-center gap-2.5">
                <Icon as={Globe} size={14} className="text-slate-400 dark:text-slate-500 shrink-0" />
                <a href={place.website} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors truncate font-medium">
                  {new URL(place.website).hostname.replace(/^www\./, '')}
                </a>
              </div>
            )}
            <div className="flex items-center gap-2.5">
              <Icon as={MapPin} size={14} className="text-slate-400 dark:text-slate-500 shrink-0" />
              {/* Falls back to a coords search URL: the lean card leans on this
                  link, and building it costs nothing. */}
              <a href={v.mapsUrl ?? `https://www.google.com/maps/search/?api=1&query=${v.lat},${v.lng}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium">
                {t('venues.viewOnMaps')}
              </a>
            </div>
          </div>

          {/* Photos, rating and hours come from a billable Google Place Details
              call, so they load only when the user asks for them. */}
          {canReveal && (
            <button onClick={onRevealDetails}
              className="w-full flex items-center justify-center gap-2 mb-3 px-3 py-2.5 rounded-xl border border-dashed border-teal-300 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 text-xs font-bold hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors cursor-pointer">
              <Icon as={Camera} size={14} />
              {t('venues.showDetails')}
              <span className="font-medium text-teal-600/70 dark:text-teal-400/60">{t('venues.showDetailsSource')}</span>
            </button>
          )}
          {place === null && (
            <p className="mb-3 text-[11px] text-slate-400 dark:text-slate-500 text-center">{t('venues.detailsUnavailable')}</p>
          )}

          {/* Notes */}
          {(v.priceInfo || v.notes) && (
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-1 mb-3">
              {v.priceInfo && <p className="text-xs text-slate-500 dark:text-slate-400">{v.priceInfo}</p>}
              {v.notes && <p className="text-xs text-slate-500 dark:text-slate-400 italic line-clamp-3 break-words">{v.notes}</p>}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800 flex-wrap">
            {actions && (
            <button onClick={actions.onToggleVisited}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors cursor-pointer ${
                v.visited
                  ? 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-red-200 hover:text-red-500'
                  : 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100'
              }`}
            >
              <Icon as={CheckCircle2} size={14} />
              {v.visited ? t('venues.markUnvisited') : t('venues.markVisited')}
            </button>
            )}

            {scheduledDays && scheduledDays.length > 0 ? (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20">
                <Icon as={CalendarDays} size={14} />
                {scheduledDays.length === 1
                  ? t('venues.day_one', { days: scheduledDays[0] })
                  : t('venues.day_other', { days: `${scheduledDays.slice(0, 2).join(', ')}${scheduledDays.length > 2 ? '…' : ''}` })}
              </span>
            ) : actions && (
              <button onClick={actions.onAddToItinerary}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors cursor-pointer">
                <Icon as={CalendarDays} size={14} />
                {t('venues.addToItinerary')}
              </button>
            )}

            <div className="flex-1" />
            {actions && (
            <DeleteVenueButton
              onConfirm={actions.onDelete}
              // The days are already on this card (the "Day 3" pill above), so
              // the warning names them instead of asking the user to remember.
              warning={scheduledDays && scheduledDays.length > 0
                ? t('venues.delete.withStops', {
                    count: scheduledDays.length,
                    days: scheduledDays.join(', '),
                  })
                : null}
            />
            )}
            {v.bookingUrl && (
              <a href={v.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn-primary gap-1.5 text-xs py-1.5">
                {t('venues.book')} <Icon as={ExternalLink} size={12} className="opacity-70" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
